import * as React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CardActions,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { ChevronRight, Info, Close } from "@mui/icons-material";
import ExpandableTagsBox from "../../../shared-components/ExpandableTagsBox";
import { formatUsageCount } from "../../../utils/formatters";

function AssignmentTemplateSummaryCard({
  assignmentTemplate,
  viewTemplateSearchParams = null,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [summaryOpen, setSummaryOpen] = React.useState(false);

  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%", // stretch to fill Grid item
      }}
      data-testid={`assignment-template-card-${assignmentTemplate.id}`}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {assignmentTemplate.name && (
          <Typography variant="h6" color="text.primary" gutterBottom>
            {assignmentTemplate.name}
          </Typography>
        )}
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          sx={{
            mt: 2,
          }}
        >
          <FruitifyIcon
            name={"School"}
            sx={{
              width: 18,
              height: 18,
              color: theme.palette.text.secondary,
            }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Subjects: </strong>
            {assignmentTemplate.subjects.map((s) => s.name).join(", ")}{" "}
          </Typography>
        </Stack>
        <Stack
          direction={"row"}
          spacing={1}
          alignItems={"center"}
          sx={{
            mt: 2,
          }}
        >
          <FruitifyIcon
            name={"School"}
            sx={{
              width: 18,
              height: 18,
              color: theme.palette.text.secondary,
            }}
          />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Level: </strong>
            {assignmentTemplate.educationLevel.level}
          </Typography>
        </Stack>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          {formatUsageCount(assignmentTemplate.popularity)}
        </Typography>
        <ExpandableTagsBox tags={assignmentTemplate.tags} />
      </CardContent>
      <CardActions sx={{ justifyContent: "flex-end", pt: 0, mt: -0.5 }}>
        <Tooltip
          title={
            !assignmentTemplate.summary
              ? "The summary will be generated soon"
              : ""
          }
          arrow
        >
          <span>
            <Button
              variant="card-action"
              color="primary"
              onClick={() => setSummaryOpen(true)}
              startIcon={<Info />}
              disabled={!assignmentTemplate.summary}
            >
              Summary
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="card-action"
          color="primary"
          onClick={() => {
            const baseUrl = `/assignments/view/template/${assignmentTemplate.id}`;

            const url = viewTemplateSearchParams
              ? `${baseUrl}?${new URLSearchParams(viewTemplateSearchParams).toString()}`
              : baseUrl;

            navigate(url);
          }}
          endIcon={<ChevronRight />}
        >
          View
        </Button>
      </CardActions>
      <Dialog
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        maxWidth="md"
        fullWidth
        data-testid="summary-modal"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5">
              {assignmentTemplate.name || "Template Summary"}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setSummaryOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {assignmentTemplate.summary}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSummaryOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default AssignmentTemplateSummaryCard;
